import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  Grid,
  Card,
  CardContent,
  IconButton,
} from '@mui/material'
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Devices as DevicesIcon,
} from '@mui/icons-material'
import { usePagination, useLoading } from '../../hooks'
import { StatusChip, RoleChip } from '../../components/common/Chips'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'
import { exportToCSV } from '../../utils/exportUtils'
import { formatDate } from '../../utils/formatters'

// Mock data
const mockAuditLogs = Array.from({ length: 30 }, (_, i) => ({
  id: i + 1,
  action: ['Login', 'User Created', 'Device Registered', 'Organization Updated', 'Password Reset', 'Access Denied'][i % 6],
  user: `User ${(i % 10) + 1}`,
  userRole: ['Super Admin', 'Admin', 'HR'][i % 3],
  organization: i % 3 === 0 ? null : `Organization ${(i % 5) + 1}`,
  device: i % 4 === 0 ? null : `Tablet-${String((i % 15) + 1).padStart(3, '0')}`,
  ipAddress: `192.168.1.${(i % 255) + 1}`,
  status: ['success', 'failed', 'warning'][i % 3],
  timestamp: `2024-01-${String((i % 30) + 1).padStart(2, '0')} ${String(i % 24).padStart(2, '0')}:${String(i % 60).padStart(2, '0')}`,
  details: `Action performed with details ${i + 1}`,
}))

const AuditLogs = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const pagination = usePagination(10)
  const [filterAction, setFilterAction] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterUserRole, setFilterUserRole] = useState('all')
  const { loading, startLoading, stopLoading } = useLoading()

  // Simulate loading
  React.useEffect(() => {
    startLoading()
    setTimeout(stopLoading, 1000)
  }, [])

  const getActionIcon = (action) => {
    const icons = {
      'Login': <SecurityIcon fontSize="small" />,
      'User Created': <PersonIcon fontSize="small" />,
      'Device Registered': <DevicesIcon fontSize="small" />,
      'Organization Updated': <BusinessIcon fontSize="small" />,
      'Password Reset': <SecurityIcon fontSize="small" />,
      'Access Denied': <ErrorIcon fontSize="small" />,
    }

    return icons[action] || <InfoIcon fontSize="small" />
  }

  const filteredLogs = mockAuditLogs.filter(log => {
    const matchesSearch = log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.ipAddress.includes(searchTerm)

    const matchesAction = filterAction === 'all' || log.action === filterAction
    const matchesStatus = filterStatus === 'all' || log.status === filterStatus
    const matchesUserRole = filterUserRole === 'all' || log.userRole === filterUserRole

    return matchesSearch && matchesAction && matchesStatus && matchesUserRole
  })

  const paginatedLogs = filteredLogs.slice(
    pagination.page * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
  )

  // Stats
  const stats = {
    totalLogs: mockAuditLogs.length,
    todayLogs: mockAuditLogs.filter(log => log.timestamp.includes('2024-01-20')).length,
    successLogs: mockAuditLogs.filter(log => log.status === 'success').length,
    failedLogs: mockAuditLogs.filter(log => log.status === 'failed').length,
  }

  // Unique actions for filter
  const uniqueActions = ['all', ...new Set(mockAuditLogs.map(log => log.action))]

  const handleExport = () => {
    const columns = [
      { label: 'Action', key: 'action' },
      { label: 'User', key: 'user' },
      { label: 'Role', key: 'userRole' },
      { label: 'Organization', key: 'organization' },
      { label: 'Device', key: 'device' },
      { label: 'IP Address', key: 'ipAddress' },
      { label: 'Status', key: 'status' },
      { label: 'Timestamp', key: 'timestamp' },
      { label: 'Details', key: 'details' }
    ]
    exportToCSV(filteredLogs, columns, 'audit-logs')
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight="bold">
              Audit Logs
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#1976d2', mr: 2 }}>
                      <SecurityIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.totalLogs}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Logs
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#2e7d32', mr: 2 }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.successLogs}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Successful
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#dc004e', mr: 2 }}>
                      <ErrorIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.failedLogs}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Failed
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#ed6c02', mr: 2 }}>
                      <WarningIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.todayLogs}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Today's Logs
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters and Search */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  placeholder="Search logs..."
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Action</InputLabel>
                  <Select
                    label="Action"
                    value={filterAction}
                    onChange={(e) => setFilterAction(e.target.value)}
                  >
                    {uniqueActions.map(action => (
                      <MenuItem key={action} value={action}>
                        {action === 'all' ? 'All Actions' : action}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="warning">Warning</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>User Role</InputLabel>
                  <Select
                    label="User Role"
                    value={filterUserRole}
                    onChange={(e) => setFilterUserRole(e.target.value)}
                  >
                    <MenuItem value="all">All Roles</MenuItem>
                    <MenuItem value="Super Admin">Super Admin</MenuItem>
                    <MenuItem value="Admin">Admin</MenuItem>
                    <MenuItem value="HR">HR</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setSearchTerm('')
                    setFilterAction('all')
                    setFilterStatus('all')
                    setFilterUserRole('all')
                  }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Audit Logs Table */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderBottom: 1, borderColor: 'divider' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
            >
              Export
            </Button>
          </Box>
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Timestamp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLogs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getActionIcon(log.action)}
                        <Box>
                          <Typography fontWeight="medium">{log.action}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {log.details}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{log.user}</Typography>
                        <RoleChip role={log.userRole} size="small" />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {log.organization ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2">{log.organization}</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.device ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DevicesIcon fontSize="small" color="action" />
                          <Typography variant="body2">{log.device}</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {log.ipAddress}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={log.status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(log.timestamp, 'dd-MM-yyyy HH:mm:ss')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredLogs.length}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />
        </Paper>
      </Paper>
    </Box>
  )
}

export default AuditLogs